import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import html2canvas from "html2canvas";
import "./Theme.css";
import "./CognitiveAssessment.css";
import { APIV4_BASE_URL } from "../apiService/apiClient";

const GOQII_LOGO = "https://appcdn.goqii.com/storeimg/21983_1778484667.png";

const CognitiveAssessment = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const { uid, goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType } = getAppParams();

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    if (lang === "ar") document.documentElement.dir = "rtl";
  }, [theme, lang]);

  const [view, setView] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [history, setHistory] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [cognitiveAgeDetails, setCognitiveAgeDetails] = useState({});
  const [gameDetails, setGameDetails] = useState([]);
  const pdfRef = useRef();

  const buildBody = useCallback(
    (extra = {}) =>
      new URLSearchParams({
        goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType,
        userId: uid,
        ...extra,
      }).toString(),
    [goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType, uid]
  );

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${APIV4_BASE_URL}/games/cognitive_game_history`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: buildBody(),
        });
        const json = await res.json();
        if (json.code === 200) setHistory(json.data.history || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [buildBody]);

  const handleViewReport = async (groupName) => {
    setView("report");
    setLoadingReport(true);
    try {
      const res = await fetch(`${APIV4_BASE_URL}/games/cognitive_single_scoreboard`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: buildBody({ groupName }),
      });
      const json = await res.json();
      if (json.code === 200) {
        setUserDetails(json.data.userDetails || {});
        setCognitiveAgeDetails(json.data.cognitiveAgeDetails || {});
        setGameDetails(json.data.gameDetails || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownload = async () => {
    const element = pdfRef.current;
    const noPrint = document.querySelectorAll(".ca-no-print");
    noPrint.forEach((el) => (el.style.visibility = "hidden"));
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      // Dynamically import jsPDF — run: npm install jspdf
      const { jsPDF } = await import("jspdf");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const contentW = pdfW - 2 * margin;
      const contentH = pdfH - 2 * margin;
      const scaleFactor = contentW / canvas.width;
      const pageHeightCanvas = contentH / scaleFactor;
      let startY = 0;
      let remaining = canvas.height;

      while (remaining > 0) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(pageHeightCanvas, remaining);
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, startY, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height);
        const imgData = pageCanvas.toDataURL("image/png");
        pdf.addImage(imgData, "PNG", margin, margin, contentW, (pageCanvas.height * contentW) / canvas.width);
        remaining -= pageHeightCanvas;
        startY += pageHeightCanvas;
        if (remaining > 0) pdf.addPage();
      }

      const link = document.createElement("a");
      link.href = pdf.output("datauristring");
      link.download = "cognitive_assessment.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("PDF generation failed:", e);
    } finally {
      noPrint.forEach((el) => (el.style.visibility = ""));
    }
  };

  /* ---- REPORT VIEW ---- */
  if (view === "report") {
    if (loadingReport) {
      return (
        <div className="ca-wrapper">
          {[1, 2, 3].map((i) => <div key={i} className="ca-shimmer-card" />)}
        </div>
      );
    }
    return (
      <div className="ca-wrapper">
        <div ref={pdfRef} className="ca-report">
          <div className="ca-report-header">
            <img src={GOQII_LOGO} alt="GOQii" className="ca-report-logo" />
          </div>

          <p className="ca-report-title">{t("cognitiveAssessment.title")}</p>
          {gameDetails[0] && (
            <p className="ca-report-date">
              {t("cognitiveAssessment.dateOfAssessment")}: {gameDetails[0].gamesPlayedDate}
            </p>
          )}

          <hr className="ca-divider ca-divider-dark" />

          <p className="ca-report-section-title">{t("cognitiveAssessment.personalDetails")}</p>
          <p className="ca-report-row"><strong>{t("cognitiveAssessment.name")}:</strong> {userDetails.name}</p>
          <p className="ca-report-row"><strong>{t("cognitiveAssessment.age")}:</strong> {userDetails.age}</p>
          <p className="ca-report-row"><strong>{t("cognitiveAssessment.gender")}:</strong> {userDetails.gender}</p>

          <p className="ca-report-section-title">{t("cognitiveAssessment.cognitiveAgeLabel")}</p>
          {cognitiveAgeDetails.image && (
            <img src={cognitiveAgeDetails.image} alt="Cognitive Age" className="ca-cognitive-img" />
          )}
          <p className="ca-report-row">
            <strong>{t("cognitiveAssessment.actualAge")}:</strong> {cognitiveAgeDetails.actualage}
          </p>
          <p className="ca-report-row">
            <strong>{t("cognitiveAssessment.cognitiveAge")}:</strong> {cognitiveAgeDetails.cognitiveage}
          </p>
          {cognitiveAgeDetails.text && (
            <p className={`ca-report-row ca-age-tag-${cognitiveAgeDetails.difference}`}>
              {cognitiveAgeDetails.text}
            </p>
          )}

          <p className="ca-report-section-title" style={{ marginTop: 14 }}>
            {t("cognitiveAssessment.cognitiveAgeDomains")}
          </p>
          {gameDetails.map((val, i) => (
            <div key={i} className="ca-score-row">
              <span className="ca-score-label">{val.category}</span>
              <span className="ca-score-value">{val.cognitiveScore}/{val.totalScore}</span>
            </div>
          ))}

          <hr className="ca-divider ca-divider-dark" style={{ marginTop: 16 }} />

          <p className="ca-report-disclaimer">
            <strong>Disclaimer:</strong>
            <ul>
              <li>The Cognitive Age Assessment is intended for informational purposes only.</li>
              <li>Results are not a medical diagnosis and may vary due to individual factors like mood or environment.</li>
              <li>This tool is not a substitute for professional advice or medical evaluation.</li>
            </ul>
          </p>
        </div>

        <div className="ca-report-actions ca-no-print">
          <button className="ca-btn-outline" onClick={() => setView("dashboard")}>
            {t("cognitiveAssessment.close")}
          </button>
          {appType?.toLowerCase() !== "ios" && (
            <button className="ca-btn-primary ca-btn-narrow" onClick={handleDownload}>
              {t("cognitiveAssessment.downloadPdf")}
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ---- DASHBOARD VIEW ---- */
  return (
    <div className="ca-wrapper">
      <div className="ca-hero">
        <img
          src="https://appcdn.goqii.com/storeimg/73829_1778484701.png"
          alt="Cognitive Assessment"
          className="ca-hero-img"
        />
      </div>
      <p className="ca-desc">{t("cognitiveAssessment.desc")}</p>
      <button
        className="ca-btn-primary"
        onClick={() =>
          window.open(
            "https://goqii.com/app?fsn=155&fssn=0&fai=%7B%22type%22%3A%22cognitive_age_intro%22%2C%22id%22%3A%22155%22%7D"
          )
        }
      >
        {t("cognitiveAssessment.startBtn")}
      </button>

      <hr className="ca-divider" />

      <p className="ca-section-title">{t("cognitiveAssessment.previousAssessments")}</p>

      {loading ? (
        [1, 2, 3].map((i) => <div key={i} className="ca-shimmer-card" />)
      ) : history.length === 0 ? (
        <p className="ca-empty-text">{t("cognitiveAssessment.noAssessments")}</p>
      ) : (
        history.map((item, i) => (
          <div key={i} className="ca-card" onClick={() => handleViewReport(item.groupName)}>
            <p className="ca-card-date">{item.gamesPlayedDate}</p>
            <div className="ca-card-row">
              <span className="ca-card-label">{t("cognitiveAssessment.cognitiveAge")}:</span>
              <span className="ca-card-value">{item.cognitiveAge}</span>
            </div>
            <div className="ca-card-actions">
              <button
                className="ca-btn-outline"
                onClick={(e) => { e.stopPropagation(); handleViewReport(item.groupName); }}
              >
                {t("cognitiveAssessment.viewReport")}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default CognitiveAssessment;
